import { Suspense, lazy, type ReactNode } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import AuthLayout from '@/layouts/AuthLayout';
import DashboardLayout from '@/layouts/DashboardLayout';

// Each page is its own dynamic chunk for route-level code splitting.
const Home = lazy(() => import('@/pages/index'));

// (auth)/* group
const Login = lazy(() => import('@/pages/(auth)/login/index'));
const Register = lazy(() => import('@/pages/(auth)/register/index'));
const ForgotPassword = lazy(() => import('@/pages/(auth)/forgot-password/index'));

// dashboard/*
const Dashboard = lazy(() => import('@/pages/dashboard/index'));
const DashboardAddresses = lazy(() => import('@/pages/dashboard/addresses/index'));
const DashboardInvoices = lazy(() => import('@/pages/dashboard/invoices/index'));
const DashboardLoyalty = lazy(() => import('@/pages/dashboard/loyalty/index'));
const DashboardNotifications = lazy(() => import('@/pages/dashboard/notifications/index'));
const DashboardOrders = lazy(() => import('@/pages/dashboard/orders/index'));
const DashboardOrderDetail = lazy(() => import('@/pages/dashboard/orders/[id]/index'));
const DashboardOrderNew = lazy(() => import('@/pages/dashboard/orders/new/index'));
const DashboardProfile = lazy(() => import('@/pages/dashboard/profile/index'));
const DashboardReferral = lazy(() => import('@/pages/dashboard/referral/index'));
const DashboardSubscriptions = lazy(() => import('@/pages/dashboard/subscriptions/index'));
const DashboardSubscriptionsNew = lazy(() => import('@/pages/dashboard/subscriptions/new/index'));
const DashboardTracking = lazy(() => import('@/pages/dashboard/tracking/[id]/index'));

// pay/*
const PayWithToken = lazy(() => import('@/pages/pay/[token]/index'));
const PayCallback = lazy(() => import('@/pages/pay/callback/index'));

const PageFallback = (
  <div className="min-h-screen flex items-center justify-center text-gray-500">Loading…</div>
);

function L(node: ReactNode) {
  return <Suspense fallback={PageFallback}>{node}</Suspense>;
}

export const router = createBrowserRouter([
  // Public landing
  { path: '/', element: L(<Home />) },

  // (auth) — login / register / forgot-password
  {
    element: <AuthLayout />,
    children: [
      { path: '/login', element: L(<Login />) },
      { path: '/register', element: L(<Register />) },
      { path: '/forgot-password', element: L(<ForgotPassword />) },
    ],
  },

  // Authenticated dashboard. DashboardLayout handles auth gating + sidebar.
  {
    path: '/dashboard',
    element: <DashboardLayout />,
    children: [
      { index: true, element: L(<Dashboard />) },
      { path: 'addresses', element: L(<DashboardAddresses />) },
      { path: 'invoices', element: L(<DashboardInvoices />) },
      { path: 'loyalty', element: L(<DashboardLoyalty />) },
      { path: 'notifications', element: L(<DashboardNotifications />) },
      {
        path: 'orders',
        children: [
          { index: true, element: L(<DashboardOrders />) },
          { path: 'new', element: L(<DashboardOrderNew />) },
          { path: ':id', element: L(<DashboardOrderDetail />) },
        ],
      },
      { path: 'profile', element: L(<DashboardProfile />) },
      { path: 'referral', element: L(<DashboardReferral />) },
      {
        path: 'subscriptions',
        children: [
          { index: true, element: L(<DashboardSubscriptions />) },
          { path: 'new', element: L(<DashboardSubscriptionsNew />) },
        ],
      },
      {
        path: 'tracking',
        children: [{ path: ':id', element: L(<DashboardTracking />) }],
      },
    ],
  },

  // Public payment flow
  { path: '/pay/:token', element: L(<PayWithToken />) },
  { path: '/pay/callback', element: L(<PayCallback />) },

  // 404 → home
  { path: '*', element: <Navigate to="/" replace /> },
]);
